import T from 'i18n-react';
import Moment from 'moment';
import { LogLevel } from '../constants/LogLevelConstants';
import { PaymentValidationResult } from '../constants/PaymentValidationResultConstants';
import { PaymentVendorProcessType } from '../constants/PaymentVendorProcessTypeConstants';
import { history } from '../index';
import * as CommonState from './Common';
import { actionCreators as commonActionCreators } from './Common';

// Import secureStorage for authentication
const { secureStorage } = CommonState;

const initialState = {
    paymentLoading: false,
    authPaymentVerified: false,
    authorizeAccountPaymentLoading: false,
    authPaymentExpired: false,
    paymentErrorMessage: '',
    paymentSuccessMessage: '',
    showAddPaymentMethod: false,
    hasRequestedPaymentAccounts: false,
    paymentAccounts: [],
    paymentAccount: {},
    accountPayCreditCardPaymentAccount: {},
    accountPayEcheckPaymentAccount: {},
    accountAutoPayment: {},
    paymentArrangement: {},
    payExtensionRenderData: {
        payExtensionMaxAnnualReq: 0,
        payExtensionMaxDays: 0,
        resourceLangKey: '',
        extendedFormattedDates: {
            extendedDateFormmat1: '',
            extendedDateFormmat2: ''
        }
    },
    showAutoPayForm: false,
    showConfirmModal: false,
    showStopAutoPayConfirmModal: false,
    showDeletePaymentAccountConfirmModal: false,
    showPaymentTypeSelectedModal: false,
    paymentHistoryList: [],
    phYears: [],
    showAccountPaymentSuccessModal: false,
    deletePaymentaccountID: 0,
    redirectToAutoPay: false,
    showPaymentAccountCreated: false,
    i4GoAuthorizeClient: {},
    showCreditCardPaymentType: false,
    showCreditCardAndEcheckPaymentType: false,
    showEcheckPaymentType: false,
    // iFrame related state
    useHostedPaymentPage: false,
    iFrameToken: '',
    iFrameResponse: {},
    hostedPaymentPageContent: null,
    paymentProviderID: null,
    vendorName: '',
    paymentValidationResult: PaymentValidationResult.Unset,
    paymentAttemptID: 0,
};

const getIFramePageConfig = (data) => {

    // Process the API response
    const config = {
        useHostedPaymentPage: data.useHostedPaymentPage || false,
        paymentProviderID: data.paymentProviderID,
        vendorName: data.vendorName,
        hostedPaymentPageContent: data.hostedPaymentPageContent
    };

    return config;
};

export const actionCreators = {

    setPaymentError: (errorMessage) => (dispatch) => {
        dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: errorMessage });
    },

    hideLoadingSpinner: () => (dispatch) => {
        dispatch({ type: 'HIDE_LOADING_SPINNER', paymentLoading: false });
    },

    runHostedPaymentPageActions: (request) => async (dispatch, getState) => {
        dispatch({ type: 'REQUEST_HOSTED_PAYMENT_PAGE_CONFIG_START' });

        try {
            const state = getState();
            const { serviceUrl } = state.common;
            const headers = { ...CommonState.headers };

            // Use const and provide a default value for actions during destructuring
            const {
                actions = [],
                accountID,
                accountNumber,
                userName,
                companyID,
                amount = -1,
                tenderTypeID,
                paymentVendorProcessTypeID,
                isPayment = false,
                saveProfile = false,
                pullAuthorizationToken = true,
                keepLoadingSpinner = false
            } = request;

            // Determine which API calls to make
            const shouldFetchSettings = actions.length === 0 || actions.includes('iFrameSettings');
            const shouldFetchValidation = actions.length === 0 || actions.includes('paymentValidation');

            const promisesToRun = [];

            // Conditionally add promises to the array
            if (shouldFetchSettings) {
                const vendorRequest = {
                    getAuthorizationToken: pullAuthorizationToken,
                    companyID: companyID,
                    tenderTypeID: tenderTypeID,
                    paymentVendorProcessTypeID: paymentVendorProcessTypeID,
                    userName: userName,
                    isPayment: isPayment,
                    saveProfile: saveProfile,
                    accountNumber: accountNumber
                };
                const params = new URLSearchParams(vendorRequest);

                const apiUrl = `${serviceUrl}/ActivePaymentVendor/?${params.toString()}`;
                const settingsPromise = fetch(apiUrl, { headers, method: 'GET', credentials: "same-origin" })
                    .then(response => CommonState.handleResponse(response, dispatch));
                promisesToRun.push(settingsPromise);
            }

            if (shouldFetchValidation) {
                // Use URLSearchParams for safer query string building
                const params = new URLSearchParams({ accountID: accountID, paymentAmount: amount, tenderTypeID: tenderTypeID });
                const apiUrl = `${serviceUrl}/PaymentValidation/?${params.toString()}`;
                const validationPromise = fetch(apiUrl, { headers, method: 'GET', credentials: "same-origin" })
                    .then(response => CommonState.handleResponse(response, dispatch));
                promisesToRun.push(validationPromise);
            }

            // Run all necessary promises in parallel
            const allResponses = await Promise.all(promisesToRun);

            // Safely access the results without bugs
            let iFrameSettingsResponse = null;
            let responseIndex = 0;

            if (shouldFetchSettings) {
                iFrameSettingsResponse = allResponses[responseIndex];
                responseIndex++; // Increment index only if the first promise was run

                const config = getIFramePageConfig({ ...iFrameSettingsResponse, ...request });

                if (config.isError) {
                    dispatch({ type: 'SET_PAYMENT_ERROR', error: 'Cannot allow payments right now' });

                    const operation = paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment ? 'createToken' : 'postPayment';
                    dispatch(commonActionCreators.postServerLog(LogLevel.Error, `Payment.runHostedPaymentPageActions (operation:${operation}, AccountID = ${state.account.accountID}): iFrame credentials misconfiguration.`, ''));
                    //we allow the execution to continue, but the iFrame form will not be rendered
                }

                // Dispatch the success action with the final payload
                dispatch({ type: 'SET_HOSTED_PAYMENT_PAGE_CONFIG', isLoading: keepLoadingSpinner, config });
            }

            if (shouldFetchValidation) {
                const { isPaymentAllowed = false, errorMessage } = allResponses[responseIndex];

                if (isPaymentAllowed) {
                    // Create a PaymentAttempt record before allowing Step 2 to render.
                    // This must succeed before the Approved result is dispatched.
                    try {
                        const attemptHeaders = { ...CommonState.headers };
                        attemptHeaders.Authorization = CommonState.secureStorage.getItem('privateToken');

                        const csrfToken = await CommonState.fetchCSRFToken();
                        if (!csrfToken) {
                            console.error('Failed to get CSRF token');
                            dispatch({ type: 'SET_HOSTED_PAYMENT_PAGE_CONFIG_ERROR', error: 'Failed to initialize the payment session. Please try again.' });
                            return;
                        }
                        attemptHeaders['X-MyAccount-CSRF-TOKEN'] = csrfToken;

                        const attemptBody = JSON.stringify({
                            accountID: accountID,
                            paymentAmount: amount,
                            tenderTypeID: tenderTypeID,
                            paymentSourceID: 2 // 2 = WEB
                        });

                        const attemptResponse = await fetch(`${serviceUrl}/PaymentAttempt`, {
                            headers: attemptHeaders,
                            method: 'POST',
                            body: attemptBody,
                            credentials: 'same-origin'
                        }).then(response => CommonState.handleResponse(response, dispatch));

                        dispatch({ type: 'SET_PAYMENT_ATTEMPT_ID', paymentAttemptID: attemptResponse.paymentAttemptID });
                        dispatch({ type: 'REQUEST_HOSTED_PAYMENT_VALIDATION_RESPONSE', isLoading: keepLoadingSpinner, paymentValidationResult: PaymentValidationResult.Approved });

                    } catch (attemptError) {
                        console.error('Failed to create PaymentAttempt:', attemptError);
                        dispatch({ type: 'SET_HOSTED_PAYMENT_PAGE_CONFIG_ERROR', error: 'Failed to initialize the payment session. Please try again.' });
                    }
                } else {
                    dispatch({ type: 'SET_PAYMENT_ERROR', error: errorMessage || 'Cannot allow payments right now' });
                }
            }

        } catch (error) {
            console.error(error);
            dispatch({
                type: 'SET_HOSTED_PAYMENT_PAGE_CONFIG_ERROR',
                error: error.message,
            });
        }
    },

    resetPaymentValidation: () => (dispatch) => {
        dispatch({ type: 'REQUEST_HOSTED_PAYMENT_VALIDATION_RESPONSE', paymentValidationResult: PaymentValidationResult.Unset });
    },

    isValidPayment: (request) => (dispatch, getState) => {
        const state = getState();

        dispatch({ type: 'REQUEST_HOSTED_PAYMENT_VALIDATION_RESPONSE' });

        const apiUrl = `${state.common.serviceUrl}/HostedPaymentAmountValidation/?AccountID=${request.accountID}&PaymentAmount=${request.amount}`;

        /*
        TenderTypeID, PaymentAccount, PaymentAmount
        */

        let headers = { ...CommonState.headers };
        headers.Authorization = secureStorage.getItem('privateToken');

        fetch(apiUrl, {
            method: 'GET',
            credentials: "same-origin",
            headers: headers
        })
            .then(data => {                                
                const isPaymentAllowed = data ? data.isPaymentAllowed : false;
                const paymentValidationResult = isPaymentAllowed ? PaymentValidationResult.Approved : PaymentValidationResult.Declined;

                // Dispatch the success action with the final payload
                dispatch({ type: 'REQUEST_HOSTED_PAYMENT_VALIDATION_RESPONSE', paymentValidationResult: paymentValidationResult });
            })
            .catch(error => {
                dispatch(commonActionCreators.postServerLog(LogLevel.Error, `Payment.isValidPayment (AccountID = ${state.account.accountID}): Cannot validate attempted payment amount.`, error.message));
                dispatch({ type: 'SET_PAYMENT_ERROR', error: error.message });
            });
    },

    setIFrameToken: (token) => (dispatch) => {
        dispatch({ type: 'SET_IFRAME_TOKEN', token });
    },

    setIFrameResponse: (response) => (dispatch) => {
        dispatch({ type: 'SET_IFRAME_RESPONSE', response });
    },

    postPaymentAccount: (paymentAccountRequest) => async (dispatch, getState) => {
        const state = getState();
        let headers = { ...CommonState.headers };
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        // force CSRF token in the header
        const csrfToken = await CommonState.fetchCSRFToken();
        if (!csrfToken) {
            console.error("Failed to get CSRF token");
            return;
        }
        headers['X-MyAccount-CSRF-TOKEN'] = csrfToken; 

        dispatch({ type: 'REQUEST_PAYMENT_START' });

        fetch(`${state.common.serviceUrl}/PaymentAccount`, {
            headers: headers,
            method: 'POST',
            body: JSON.stringify(paymentAccountRequest),
            credentials: "same-origin"
        })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {
            console.log('PaymentAccount created successfully:', data);
            dispatch({ type: 'SET_PAYMENT_SUCCESS', message: 'Payment method created successfully' });
            
            // Refresh payment accounts list to show the new payment method
            dispatch(actionCreators.getPaymentAccounts());
            
            // Close the add payment method form
            dispatch({ type: 'TOGGLE_ADD_PAYMENT_METHOD', showAddPaymentMethod: false });
        })
        .catch(error => {
            console.error('Error creating PaymentAccount:', error);
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });
    },

    savePaymentAccount: (pa) => (dispatch, getState) => {

        const state = getState();
        let headers = CommonState.headers;
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        let paymentAccount = {};
        paymentAccount.address = {};
        paymentAccount.accountID = state.common.accountID;
        paymentAccount.active = true;
        paymentAccount.LastUpdateUserName = state.common.account.person.fullName + ' via My Account';

        //CV ZD14560 2020/03/26 adding this condition when is credit card and has stripePK to build the object and send it to the api
        if (pa.paymentAccountTypeID === '3' && pa.paymentStripePK) {
            paymentAccount.paymentAccountTypeID = 3;
            paymentAccount.nameOnAccount = pa.cardNameOnAccount;
            paymentAccount.accountName = pa.cardAccountName;
            paymentAccount.ccv = pa.ccv;
            paymentAccount.address.address1 = pa.address1;
            paymentAccount.address.address2 = pa.address2;
            paymentAccount.address.city = pa.city;
            paymentAccount.address.state = pa.state;
            paymentAccount.address.postalCode = pa.postalCode;
            paymentAccount.billingZipCode = pa.postalCode;
            paymentAccount.paymentToken = pa.paymentToken;

        } else if (pa.paymentAccountTypeID === '3' && !pa.paymentStripePK) { //credit card

            paymentAccount.accountNumber = pa.cardNumberValue.replace(/-/g, '').replace(/_/g, '');
            let cardTypeID = CommonState.getCardType(paymentAccount.accountNumber);

            if (cardTypeID === '4' && pa.ccv.length < 4) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: T.translate('paymentMethod.invalidAmexCcv') });
                return;
            } else if (cardTypeID !== '4' && pa.ccv.length !== 3) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: T.translate('paymentMethod.invalidCcv') });
                return;
            }

            if (!state.common.systemProperties.paymentCardTypes.some(x => x === cardTypeID)) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: T.translate('paymentMethod.invalidCardType') });
                return;
            }

            paymentAccount.cardTypeID = cardTypeID;
            paymentAccount.paymentAccountTypeID = 3;
            paymentAccount.nameOnAccount = pa.cardNameOnAccount;
            paymentAccount.accountName = pa.cardAccountName;
            paymentAccount.ccv = pa.ccv;
            paymentAccount.address.address1 = pa.address1;
            paymentAccount.address.address2 = pa.address2;
            paymentAccount.address.city = pa.city;
            paymentAccount.address.state = pa.state;
            paymentAccount.address.postalCode = pa.postalCode;
            paymentAccount.billingZipCode = pa.postalCode;

            let expiration = pa.expiration.replace(/_/g, '').split('/');

            if (expiration.length === 2) {

                const expireMonth = parseInt(expiration[0]);
                const expireYear = parseInt(expiration[1]);

                if (!isNaN(expireMonth) && !isNaN(expireYear)) {
                    paymentAccount.expireMonth = expireMonth;
                    paymentAccount.expireYear = 2000 + expireYear;
                }
            }

        } else if (pa.paymentAccountTypeID === '4') { //e-check
            paymentAccount.paymentAccountTypeID = 4;
            paymentAccount.accountNumber = pa.bankAccountNumber;
            paymentAccount.routingNumber = pa.bankRoutingNumber.replace(/_/g, '');
            paymentAccount.accountName = pa.bankAccountName;
            paymentAccount.nameOnAccount = pa.bankNameOnAccount;
            paymentAccount.bankAccountType = pa.bankAccountType;

            if (state.common.paymentStripe) {
                paymentAccount.paymentToken = pa.public_token + '|' + pa.account_id;
            }
        }

        let request = {};
        request.paymentAccount = paymentAccount;
        request.accountID = state.common.accountID;


        //if i4go payment enabled and cc and we got access block try to get card unique id
        if (state.payment.i4GoAuthorizeClient.i4go_isEnable && state.payment.i4GoAuthorizeClient.i4go_accessblock && pa.paymentAccountTypeID === '3') {

            let requestI4Go = CommonState.getI4GoCardEntryRequest(state.payment.i4GoAuthorizeClient.i4go_accessblock, paymentAccount.cardTypeID,
                paymentAccount.accountNumber, paymentAccount.expireMonth, paymentAccount.expireYear, paymentAccount.ccv);

            var queryString = Object.keys(requestI4Go).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(requestI4Go[key])).join('&');

            fetch(`${state.payment.i4GoAuthorizeClient.i4go_server}/index.cfm?${queryString}`, { method: 'GET' })
                .then(response => CommonState.handleResponse(response, dispatch))
                .then(data => {

                    if (data.i4go_responsecode === '1' && data.i4go_uniqueid) {

                        request.paymentAccount.paymentToken = data.i4go_uniqueid;
                        request.paymentAccount.i4GoCardType = CommonState.getI4GoCardType(paymentAccount.cardTypeID);
                        fetchPaymentAccount(dispatch, getState, request);

                    } else {
                        dispatch({ type: 'SET_PAYMENT_ERROR', errorMessage: data.i4go_responsetext });
                    }
                })
                .catch(function (error) {
                    console.log(error);
                    dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
                });

            dispatch({ type: 'REQUEST_PAYMENT_START' });

        } else {
            fetchPaymentAccount(dispatch, getState, request);
        }
    },

    deletePaymentAccount: () => (dispatch, getState) => {

        const state = getState();
        let headers = CommonState.headers;
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        let paymentAccount = {};
        paymentAccount.LastUpdateUserName = state.common.account.person.fullName + ' via My Account';

        let request = {};
        request.paymentAccountID = state.payment.deletePaymentAccountID;
        request.accountID = state.common.accountID;
        request.paymentAccount = paymentAccount;

        //TODO: review webmethod
        fetch(`${state.common.serviceUrl}/PaymentAccount`, { headers: headers, method: 'DELETE', body: JSON.stringify(request), credentials: "same-origin" })
            .then(response => CommonState.handleResponse(response, dispatch))
            .then(data => {
                toggleDeletePaymentAccountConfirmModal(dispatch, getState, 0);
                getPaymentAccounts(dispatch, getState);
            })
            .catch(function (error) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
            });

        dispatch({ type: 'REQUEST_PAYMENT_START' });

    },

    getI4GoAuthorizeClient: () => (dispatch, getState) => {

        const state = getState();

        if (state.payment.i4GoAuthorizeClient.i4go_isEnable === false) {
            return;
        }

        fetch(`api/data/I4GoAuthorizeClient`, { credentials: "same-origin" })
            .then(response => CommonState.handleResponse(response, dispatch))
            .then(data => {
                if (data.i4go_responsecode === 1 || data.i4go_responsecode === -1) {
                    dispatch({ type: 'RESPONSE_I4GO_RESULT', i4GoAuthorizeClient: data });
                } else {
                    dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: data.i4go_responsetext });
                }
            })
            .catch(function (error) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
            });

        dispatch({ type: 'REQUEST_PAYMENT_START' });
    },

    getPaymentAccounts: () => (dispatch, getState) => {
        getPaymentAccounts(dispatch, getState);
    },

    getSuggestedPaymentObject: () => (dispatch, getState) => {
        getSuggestedPaymentObject(dispatch, getState);
    },

    getAccountAutoPayment: () => (dispatch, getState) => {
        getAccountAutoPayment(dispatch, getState);
    },

    getPaymentHistory: () => (dispatch, getState) => {
        getPaymentHistory(dispatch, getState);
    },

    getPaymentExtensionData: () => (dispatch, getState) => {
        getPaymentExtensionData(dispatch, getState);
    },

    postPaymentExtension: () => (dispatch, getState) => {
        postPaymentExtension(dispatch, getState);
    },

    toggleAddPaymentMethod: () => (dispatch, getState) => {
        toggleAddPaymentMethod(dispatch, getState);
    },

    toggleShowAutoPayForm: () => (dispatch, getState) => {
        toggleShowAutoPayForm(dispatch, getState);
    },

    toggleStopAutoPayConfirmModal: () => (dispatch, getState) => {
        toggleStopAutoPayConfirmModal(dispatch, getState);
    },

    toggleDeletePaymentAccountConfirmModal: (deletePaymentAccountID) => (dispatch, getState) => {
        toggleDeletePaymentAccountConfirmModal(dispatch, getState, deletePaymentAccountID);
    },

    toggleAccountPaymentSuccessModal: () => (dispatch, getState) => {
        toggleAccountPaymentSuccessModal(dispatch, getState);
    },

    togglePaymentTypeSelectModal: () => (dispatch, getState) => {
        togglePaymentTypeSelectModal(dispatch, getState);
    },

    autoPayInit: () => (dispatch, getState) => {

        const state = getState();
        let headers = CommonState.headers;
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        let fetchPaymentAccounts = fetch(`${state.common.serviceUrl}/PaymentAccounts?AccountID=${state.common.accountID}`, { headers: headers, method: 'GET', credentials: "same-origin" })
            .then(response => CommonState.handleResponse(response, dispatch));

        let fetchAccountAutopayment = fetch(`${state.common.serviceUrl}/AccountAutopayment?AccountID=${state.common.accountID}`, { headers: headers, method: 'GET', credentials: "same-origin" })
            .then(response => CommonState.handleResponse(response, dispatch));

        //call both methods and when got all results process them
        //TODO: review webmethods
        Promise.all([fetchPaymentAccounts, fetchAccountAutopayment])
            .then(([paymentAccounts, accountAutoPayment]) => {

                dispatch({ type: 'SET_PAYMENT_ACCOUNTS', paymentAccounts: paymentAccounts });

                if (paymentAccounts && paymentAccounts.length > 0) {
                    dispatch({ type: 'SET_PAYMENT_ACCOUNT', paymentAccount: paymentAccounts[0] });
                }

                if (accountAutoPayment) {
                    dispatch({ type: 'SET_ACCOUNT_AUTO_PAYMENT', accountAutoPayment: accountAutoPayment });
                } else {
                    dispatch({ type: 'SET_ACCOUNT_AUTO_PAYMENT', accountAutoPayment: {} });
                }

            })
            .catch(function (error) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
            });

        dispatch({ type: 'REQUEST_PAYMENT_START' });

    },

    changePaymentAccount: (pa) => (dispatch, getState) => {
        dispatch({ type: 'SET_PAYMENT_ACCOUNT', paymentAccount: pa });
    },

    changeCreditCardPaymentAccount: (pa) => (dispatch, getState) => {
        dispatch({ type: 'SET_ACCOUNT_PAY_PAYMENT_ACCOUNT_CC', accountPayCreditCardPaymentAccount: pa });
    },

    changeEcheckPaymentAccount: (pa) => (dispatch, getState) => {
        dispatch({ type: 'SET_ACCOUNT_PAY_PAYMENT_ACCOUNT_ECHECK', accountPayEcheckPaymentAccount: pa });
    },

    changePaymentAccountByID: (id) => (dispatch, getState) => {

        const state = getState();
        let pa = state.payment.paymentAccounts.filter(x => x.paymentAccountID === id);

        if (pa && pa.length === 1) {
            dispatch({ type: 'SET_PAYMENT_ACCOUNT', paymentAccount: pa[0] });
        }

    },

    saveAutoPayment: (ap) => (dispatch, getState) => {

        const state = getState();
        let headers = CommonState.headers;
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        let toggleForm = false;
        let autoPay = {};

        if (state.payment.accountAutoPayment.accountAutoPayID > 0) {
            autoPay.accountAutoPayID = state.payment.accountAutoPayment.accountAutoPayID;
            toggleForm = true;
        }

        autoPay.accountID = state.common.accountID;
        autoPay.startDate = Moment();
        autoPay.active = true;
        autoPay.paymentAccountID = state.payment.paymentAccount.paymentAccountID;
        autoPay.LastUpdatePersonID = state.common.account.person.personID;
        autoPay.Source = "MyAccount";

        //if postpay account then set default trigger (Bill Due Date) and pay amount (0)
        if (state.common.account.accountBillingTypeID === 2) {

            autoPay.systemEventTriggerID = 1006; //Bill Due Date
            autoPay.autoPayAmount = 0;
            autoPay.balanceThreshold = 0;

        } else { //this is only for prepay

            autoPay.autoPayAmount = 40; //default value

            if (ap.autoPayAmount && !isNaN(parseFloat(ap.autoPayAmount))) {
                autoPay.autoPayAmount = parseFloat(ap.autoPayAmount);
            }

            if (ap.threshold === '1') {

                autoPay.systemEventTriggerID = 261;
                autoPay.balanceThreshold = 40; //default value

                if (ap.balanceThresholdAmount && !isNaN(parseFloat(ap.balanceThresholdAmount))) {
                    autoPay.balanceThreshold = parseFloat(ap.balanceThresholdAmount);
                }

            } else if (ap.threshold === '2') {


                if (ap.daysRemainingOption === '10') {
                    autoPay.systemEventTriggerID = 60;
                } else if (ap.daysRemainingOption === '7') {
                    autoPay.systemEventTriggerID = 70;
                } else if (ap.daysRemainingOption === '5') {
                    autoPay.systemEventTriggerID = 80;
                } else if (ap.daysRemainingOption === '3') {
                    autoPay.systemEventTriggerID = 90;
                } else if (ap.daysRemainingOption === '2') {
                    autoPay.systemEventTriggerID = 100;
                }

            }

        }

        let request = {};
        request.autoPay = autoPay;
        request.accountID = state.common.accountID;
        request.languageID = state.common.langID;

        //TODO: review webmethod
        fetch(`${state.common.serviceUrl}/AutoPayment`, { headers: headers, method: 'POST', body: JSON.stringify(request), credentials: "same-origin" })
            .then(response => CommonState.handleResponse(response, dispatch))
            .then(data => {

                if (toggleForm) {
                    toggleShowAutoPayForm(dispatch, getState);
                }

                setRedirectToAutoPay(dispatch, false, false);
                getAccountAutoPayment(dispatch, getState);

            })
            .catch(function (error) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
            });

        dispatch({ type: 'REQUEST_PAYMENT_START' });

    },

    deleteAutoPayment: () => (dispatch, getState) => {

        const state = getState();
        let headers = CommonState.headers;
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        let autoPay = {};
        autoPay.LastUpdatePersonID = state.common.account.person.personID;
        autoPay.Source = "MyAccount";

        let request = {};
        request.autoPay = autoPay;
        request.accountAutoPayID = state.payment.accountAutoPayment.accountAutoPayID;
        request.accountID = state.common.accountID;

        //TODO: review webmethod
        fetch(`${state.common.serviceUrl}/AutoPayment`, { headers: headers, method: 'DELETE', body: JSON.stringify(request), credentials: "same-origin" })
            .then(response => CommonState.handleResponse(response, dispatch))
            .then(data => {
                toggleStopAutoPayConfirmModal(dispatch, getState);
                getAccountAutoPayment(dispatch, getState);
            })
            .catch(function (error) {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
            });

        dispatch({ type: 'REQUEST_PAYMENT_START' });

    },

  postAccountPay: (request) => (dispatch, getState) => {
        const state = getState();
        let headers = CommonState.headers;
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        request.ipAddress = state.common.ip;
        request.accountID = state.common.accountID;

        // Attach the PaymentAttemptID recorded during Step 1 validation (HPP workflow)
        if (state.payment.paymentAttemptID > 0) {
            request.paymentAttemptID = state.payment.paymentAttemptID;
        }

        // Only process card data if NOT using iFrame (iFrame handles this internally)
        if (request.paymentMethod !== 'iFrameCreditCard' && request.paymentMethod !== 'iFrameECheck') {
            if (request.tenderTypeID === 1) {
                if (request.cardNumberValue) {
                    request.cardNumber = request.cardNumberValue.replace(/-/g, '');
                }

                if (request.expiration && request.expiration.split('/').length === 2) {
                    request.expireMonth = request.expiration.split('/')[0];
                    request.expireYear = request.expiration.split('/')[1];
                }

                //remove these props that are not needed in the request
                delete request.cardNumberValue;
                delete request.expiration;
            }
        }

        request.paymentAmount = request.totalPayment;
        if (request.paymentAmount <= 0) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: T.translate('accountPay.paymentAmountGreaterThanZero') });
            return;
        }

        // Handle iframe hosted payment page response
        if (request.paymentMethod === 'iFrameCreditCard' || request.paymentMethod === 'iFrameECheck') {
            request.isHostedPaymentPageResponse = true;
            request.hostedPaymentPageResponse = request.hostedPaymentPageResponse || request.transactionResponse || '';
            
            // For iFrame payments, use the token and response data
            if (request.paymentToken) {
                request.paymentToken = request.paymentToken;
            }
        } else {
            // For non-hosted payment pages, set to false
            request.isHostedPaymentPageResponse = false;
            request.hostedPaymentPageResponse = '';
        }
        
        // Set referenceNumber and notes based on payment type
        if (request.isHostedPaymentPageResponse) {
            request.referenceNumber = request.referenceNumber || `iFrame Payment - ${new Date().toISOString()}`;
            request.notes = request.notes || `iFrame hosted payment page transaction`;
        } else {
            request.referenceNumber = `MyAccount Payment - ${new Date().toISOString()}`;
            request.notes = `Payment via MyAccount portal`;
        }

        request.customPaymentDistributions = request.accountPaymentDistributions;
        request.accountPaymentDistributions.map(function (paymentDistribution) {
            paymentDistribution.accountID = state.common.accountID;
            paymentDistribution.createdDate = new Date();
        });

        if (!request.anotherPaymentMethod) {

            request.usePaymentProfile = true;

            if (request.tenderTypeID === 1) {
                request.profileIdentifier = state.payment.accountPayCreditCardPaymentAccount.externalProfileIdentifier;
            } else if (request.tenderTypeID === 2) {
                request.profileIdentifier = state.payment.accountPayEcheckPaymentAccount.externalProfileIdentifier;
            }

        } else if (state.common.paymentStripe && request.public_token && request.account_id && !request.paymentStripePK) {
            request.paymentToken = request.public_token + '|' + request.account_id;
        }

        // Handle iFrame payments with clean payload
        if (request.paymentMethod === 'iFrameCreditCard' || request.paymentMethod === 'iFrameECheck') {
            // Create clean payload for iFrame payments - only required properties per API specification
            let iFrameRequest = {
                accountID: request.accountID,
                tenderTypeID: request.tenderTypeID,
                paymentAmount: request.paymentAmount,
                notes: request.notes || `iFrame hosted payment page transaction`,
                isHostedPaymentPageResponse: true,
                hostedPaymentPageResponse: request.hostedPaymentPageResponse || request.transactionResponse || '',
                paymentToken: request.paymentToken,
                nameOnAccount: request.nameOnAccount,
                paymentAttemptID: request.paymentAttemptID || 0
            };

            if (!iFrameRequest.paymentToken)
                iFrameRequest.referenceNumber = request.referenceNumber || `iFrame Payment - ${new Date().toISOString()}`;

            // Handle card details based on payment type
            if (request.tenderTypeID === 1) {
                // For credit card payments, send CardNumber, ExpireYear, ExpireMonth, and CardTypeID
                iFrameRequest.cardNumber = request.cardNumber || '';
                iFrameRequest.expireYear = request.expireYear || '';
                iFrameRequest.expireMonth = request.expireMonth || '';

                // Add cardTypeID if it was provided
                if (request.cardTypeID) {
                    iFrameRequest.cardTypeID = request.cardTypeID;
                }
            } else if (request.tenderTypeID === 2) {
                // For eCheck payments, only send CardNumber with the account number from iFrame
                iFrameRequest.accountNumber = request.accountNumber || '';
            }

            fetchAccountPayment(dispatch, getState, iFrameRequest);
            
        } else if (state.payment.i4GoAuthorizeClient.i4go_isEnable && state.payment.i4GoAuthorizeClient.i4go_accessblock && request.tenderTypeID === 1 && !request.usePaymentProfile) {

            let cardTypeID = CommonState.getCardType(request.cardNumber);

            let requestI4Go = CommonState.getI4GoCardEntryRequest(state.payment.i4GoAuthorizeClient.i4go_accessblock, cardTypeID,
                request.cardNumber, request.expireMonth, request.expireYear, request.ccv);

            var queryString = Object.keys(requestI4Go).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(requestI4Go[key])).join('&');

            // Start loading state for i4Go API call
            dispatch({ type: 'REQUEST_PAYMENT_START' });

            fetch(`${state.payment.i4GoAuthorizeClient.i4go_server}/index.cfm?${queryString}`, { method: 'GET' })
                .then(response => CommonState.handleResponse(response, dispatch))
                .then(data => {

                    if (data.i4go_responsecode === '1' && data.i4go_uniqueid) {

                        //only send required fields
                        let req = {};
                        req.paymentAmount = request.paymentAmount;
                        req.paymentToken = data.i4go_uniqueid;
                        req.ipAddress = request.ipAddress;
                        req.accountID = request.accountID;
                        req.cardZipCode = request.cardZipCode;
                        req.nameOnAccount = request.nameOnAccount;
                        req.tenderTypeID = request.tenderTypeID;
                        req.i4GoCardType = CommonState.getI4GoCardType(cardTypeID);

                        fetchAccountPayment(dispatch, getState, req);

                    } else {
                        dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: data.i4go_responsetext });
                    }

                })
                .catch(function (error) {
                    console.log(error);
                    dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
                });

            dispatch({ type: 'REQUEST_PAYMENT_START' });

        } else {
            fetchAccountPayment(dispatch, getState, request);
        }

    },

    clearPaymentState: () => (dispatch) => {
        dispatch({ type: 'CLEAR_PAYMENT_STATE' });
    },

    goToPaymentMethod: (redirect) => (dispatch) => {
        setRedirectToAutoPay(dispatch, redirect, false);
        history.push('/myaccount/paymentmethods');
    },

    authorizeAccountPayment: (request) => (dispatch, getState) => {

        const state = getState();
        let headers = CommonState.headers;
        headers.Authorization = CommonState.secureStorage.getItem('privateToken');

        request.ipAddress = state.common.ip;
        request.accountID = state.common.accountID;

        if (request.tenderTypeID === 1) {
            if (request.cardNumberValue) {
                request.cardNumber = request.cardNumberValue.replace(/-/g, '');
            }

            if (request.expiration && request.expiration.split('/').length === 2) {
                request.expireMonth = request.expiration.split('/')[0];
                request.expireYear = request.expiration.split('/')[1];
            }

            //remove these props that are not needed in the request
            delete request.cardNumberValue;
            delete request.expiration;
        }

        request.paymentAmount = request.totalPayment;
        /*if (request.paymentAmount <= 0) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: T.translate('accountPay.paymentAmountGreaterThanZero') });
            return;
        }*/

        request.customPaymentDistributions = request.accountPaymentDistributions;
        request.accountPaymentDistributions.map(function (paymentDistribution) {
            paymentDistribution.accountID = state.common.accountID;
            paymentDistribution.createdDate = new Date();
        });

        if (!request.anotherPaymentMethod) {

            request.usePaymentProfile = true;

            if (request.tenderTypeID === 1) {
                request.profileIdentifier = state.payment.accountPayCreditCardPaymentAccount.externalProfileIdentifier;
            } else if (request.tenderTypeID === 2) {
                request.profileIdentifier = state.payment.accountPayEcheckPaymentAccount.externalProfileIdentifier;
            }

        } else if (state.common.paymentStripe && request.public_token && request.account_id && !request.paymentStripePK) {
            request.paymentToken = request.public_token + '|' + request.account_id;
        }

        var requestBody = JSON.stringify(request, (key, value) => {
            if (key === "stripe" || key === "elements" || key === "cardNumberElement" || key === "cardExpiryElement" || key === "cardCvcElement") return null;
            else return value;
        });

        fetch(`${state.common.serviceUrl}/AuthorizeAccountPayment`, { headers: headers, method: 'POST', body: requestBody, credentials: "same-origin" })
            .then(response => CommonState.handleResponse(response, dispatch))
            .then(data => {

                if (data.isApproved) {
                    dispatch({ type: 'AUTH_PAYMENT_SUCCESS' });
                } else {
                    dispatch({ type: 'SET_AUTH_PAYMENT_ERROR', paymentErrorMessage: T.translate('accountPay.accountInfoNotAuthorized') });
                }
            })
            .catch(function (error) {
                dispatch({ type: 'SET_AUTH_PAYMENT_ERROR', paymentErrorMessage: error.message });
            });

        dispatch({ type: 'REQUEST_AUTH_PAYMENT_START' });
    },

    authorizeAccountPaymentExpired: () => (dispatch) => {
        dispatch({ type: 'AUTH_PAYMENT_EXPIRED' });
    },

    authorizeAccountPaymentByPass: () => (dispatch) => {
        dispatch({ type: 'AUTH_PAYMENT_BYPASS' });
    },

};

var fetchPaymentAccount = async function (dispatch, getState, req) {

    const state = getState();
    let headers = { ...CommonState.headers };
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');

    // force CSRF token in the header
    const csrfToken = await CommonState.fetchCSRFToken();
    if (!csrfToken) {
        console.error("Failed to get CSRF token");
        return;
    }
    headers['X-MyAccount-CSRF-TOKEN'] = csrfToken; 

    fetch(`${state.common.serviceUrl}/PaymentAccount`, { headers: headers, method: 'POST', body: JSON.stringify(req), credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {
            getPaymentAccounts(dispatch, getState, true);
            toggleAddPaymentMethod(dispatch, getState);
        })
        .catch(function (error) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });

    dispatch({ type: 'REQUEST_PAYMENT_START' });

};

var fetchAccountPayment = async function (dispatch, getState, req) {

    const state = getState();
    let headers = { ...CommonState.headers };
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');

    //CV ZD14560 2020/03/26 adding this to exclude the stripe objects from the request body, and prevent the circular reference problem
    var requestBody = JSON.stringify(req, (key, value) => {
        if (key === "stripe" || key === "elements" || key === "cardNumberElement" || key === "cardExpiryElement" || key === "cardCvcElement") return null;
        else return value;
    });

    // force CSRF token in the header
    const csrfToken = await CommonState.fetchCSRFToken();
    if (!csrfToken) {
        console.error("Failed to get CSRF token");
        return;
    }
    headers['X-MyAccount-CSRF-TOKEN'] = csrfToken; 

    // Start loading state
    dispatch({ type: 'REQUEST_PAYMENT_START' });

    let apiUrl = `${state.common.serviceUrl}/${req.isHostedPaymentPageResponse ? 'ApprovedAccountPayment' : 'AccountPayment'}`;

    fetch(apiUrl, { headers: headers, method: 'POST', body: requestBody, credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {

            if (data.isApproved) {
                dispatch({ type: 'SET_ACCOUNT_PAY_SUCCESS', accountPaymentSuccess: true, accountPayConfirmationNumber: data.paymentConfirmationNumber });
                toggleAccountPaymentSuccessModal(dispatch, getState);
            } else {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: data.errorMessage });
            }
        })
        .catch(function (error) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });

};

var setRedirectToAutoPay = function (dispatch, redirect, acctCreated) {
    dispatch({ type: 'SET_ACCOUNT_REDIRECT_TO_AUTOPAY', redirectToAutoPay: redirect, showPaymentAccountCreated: acctCreated });
};

var getPaymentAccounts = function (dispatch, getState, redirect) {

    const state = getState();
    let headers = CommonState.headers;
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');

    //TODO: review webmethod
    fetch(`${state.common.serviceUrl}/PaymentAccounts?AccountID=${state.common.accountID}`, { headers: headers, method: 'GET', credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {
            dispatch({ type: 'SET_PAYMENT_ACCOUNTS', paymentAccounts: data });

            if (data && data.length > 0) {
                dispatch({ type: 'SET_PAYMENT_ACCOUNT', paymentAccount: data[0] });
            }

            setPaymentType(dispatch, data);

            if (redirect && state.payment.redirectToAutoPay) {
                setRedirectToAutoPay(dispatch, false, true);
                history.push('/myaccount/autopay');
            }

        })
        .catch(function (error) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });

    dispatch({ type: 'REQUEST_PAYMENT_START' });

};

var getSuggestedPaymentObject = function (dispatch, getState) {

    const state = getState();
    let headers = CommonState.headers;
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');
    let suggestedPaymentRequest = fetch(`${state.common.serviceUrl}/SuggestedPayment?AccountID=${state.common.accountID}`, { headers: headers, method: 'GET', credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch));
    suggestedPaymentRequest.then(suggestedPaymentObject => {
        dispatch({ type: 'SET_SUGGESTED_PAYMENT_OBJECT', suggestedPaymentObject: suggestedPaymentObject });
    }).catch(function (error) {
        dispatch({ type: 'SET_PAYMENT_ERROR', accountErrorMessage: error.message });
    });

    dispatch({ type: 'REQUEST_PAYMENT_START' });
};

var getAccountAutoPayment = function (dispatch, getState) {

    const state = getState();
    let headers = CommonState.headers;
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');

    //TODO: review webmethod
    fetch(`${state.common.serviceUrl}/AccountAutopayment?AccountID=${state.common.accountID}`, { headers: headers, method: 'GET', credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {
            dispatch({ type: 'SET_ACCOUNT_AUTO_PAYMENT', accountAutoPayment: data });
        })
        .catch(function (error) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });

    dispatch({ type: 'REQUEST_PAYMENT_START' });

};

var getPaymentHistory = function (dispatch, getState) {

    const state = getState();
    let headers = CommonState.headers;
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');

    var from = new Date();
    var to = new Date();

    var paymentHistoryMonthsRange = state.common.paymentHistoryMonthsRange * -1;

    from.setHours(0, 0, 0, 0);
    from = CommonState.addMonthsToDate(from, paymentHistoryMonthsRange);

    var fromString = CommonState.formatDate(from, 'MM/DD/YYYY');
    var toString = CommonState.formatDate(to, 'MM/DD/YYYY');

    var request = { AccountID: state.common.accountID, FromDate: fromString, ToDate: toString };

    var queryString = Object.keys(request).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(request[key])).join('&');

    fetch(`${state.common.serviceUrl}/PaymentHistory?${queryString}`, { headers: headers, method: 'GET', credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {
            var rows = data.accountTransactions;
            var years = [];

            rows.map(function (row, i) {
                row.year = CommonState.formatDate(row.transactionDate, 'YYYY');
                if (!years.includes(row.year))
                    years.push(row.year);
            });

            years.sort();
            years.reverse();

            dispatch({ type: 'RESPONSE_PAYMENT_HISTORY', paymentHistoryList: rows, years });
        })
        .catch(function (error) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });

    dispatch({ type: 'REQUEST_PAYMENT_START' });

};

var getPaymentExtensionData = function (dispatch, getState) {

    const state = getState();
    let headers = CommonState.headers;
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');

    var request = { AccountID: state.common.accountID };

    var queryString = Object.keys(request).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(request[key])).join('&');


    fetch(`${state.common.serviceUrl}/PaymentExtensionData?${queryString}`, { headers: headers, method: 'GET', credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {
            var resourceLangKey = '';

            switch (data.errorMessage) {
                case "THERE_IS_NOT_UNPAID_BILL":
                    resourceLangKey = 'paymentExtension.rejectedThereIsNotUnpaidBill';
                    break;
                case "MAX_REQUEST_PER_YEAR_REACHED":
                    resourceLangKey = 'paymentExtension.rejectedMaxRequestPerYearReached';
                    break;
                case "PAYMENT_EXTENSION_ALREADY_EXISTS":
                    resourceLangKey = 'paymentExtension.rejectedPaymentExtensionAlreadyExists';
                    break;
                case "OVERDUE_BILL_EXCEEDS_EXTENSION_MAX_DAYS":
                    resourceLangKey = 'paymentExtension.rejectedOverdueBillExceedsExtesionMaxDays';
                    break;
                default:
                    break;
            }

            var errorMessage = data.errorMessage && resourceLangKey === '' ? data.errorMessage : '';
            var extensionDate = data.paymentArrangement === null ? undefined : data.paymentArrangement.extensionDate;

            dispatch({
                type: 'RESPONSE_PAYMENT_EXTENSION_DATA', paymentArrangement: data.paymentArrangement,
                payExtensionRenderData: {
                    payExtensionMaxAnnualReq: data.payExtensionMaxAnnualReq,
                    payExtensionMaxDays: data.payExtensionMaxDays,
                    disconnectDate: data.disconnectDate,
                    paymentDueDate: data.paymentDueDate,
                    resourceLangKey: resourceLangKey,
                    extendedFormattedDates: {
                        extendedDateFormmat1: CommonState.formatDate(extensionDate, 'MM-DD-YYYY'),
                        extendedDateFormmat2: CommonState.formatDate(extensionDate, 'MM/DD/YY')
                    }
                },
                paymentErrorMessage: errorMessage
            });
        })
        .catch(function (error) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });

    dispatch({ type: 'REQUEST_PAYMENT_START' });

};

var postPaymentExtension = function (dispatch, getState) {

    const state = getState();
    let headers = CommonState.headers;
    headers.Authorization = CommonState.secureStorage.getItem('privateToken');

    var request = { paymentArrangement: state.payment.paymentArrangement, accountID: state.common.accountID };

    fetch(`${state.common.serviceUrl}/PostPaymentArrangement`, { headers: headers, method: 'POST', body: JSON.stringify(request), credentials: "same-origin" })
        .then(response => CommonState.handleResponse(response, dispatch))
        .then(data => {
            if (data.paymentArrangement) {
                dispatch({ type: 'TOGGLE_PAYMENT_EXTENSION_RESULT_CONFIRM', showConfirmModal: true, paymentLoading: false });
            }
            else {
                dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: 'Unexpeted error' });
            }
        })
        .catch(function (error) {
            dispatch({ type: 'SET_PAYMENT_ERROR', paymentErrorMessage: error.message });
        });

    dispatch({ type: 'REQUEST_PAYMENT_START' });
};

var toggleAddPaymentMethod = function (dispatch, getState) {
    const state = getState().payment;
    dispatch({ type: 'TOGGLE_ADD_PAYMENT_METHOD', showAddPaymentMethod: !state.showAddPaymentMethod });
};

var toggleShowAutoPayForm = function (dispatch, getState) {
    const state = getState().payment;
    dispatch({ type: 'TOGGLE_SHOW_AUTO_PAYMENT_FORM', showAutoPayForm: !state.showAutoPayForm });
};

var toggleStopAutoPayConfirmModal = function (dispatch, getState) {
    const state = getState().payment;
    dispatch({ type: 'TOGGLE_STOP_AUTO_PAYMENT_CONFIRM', showStopAutoPayConfirmModal: !state.showStopAutoPayConfirmModal });
};

var toggleAccountPaymentSuccessModal = function (dispatch, getState) {
    const state = getState().payment;
    dispatch({ type: 'TOGGLE_ACCOUNT_PAYMENT_SUCCESS_MODAL', showAccountPaymentSuccessModal: !state.showAccountPaymentSuccessModal });
};

var toggleDeletePaymentAccountConfirmModal = function (dispatch, getState, deletePaymentAccountID) {
    const state = getState().payment;
    dispatch({ type: 'TOGGLE_DELETE_PAYMENT_ACCOUNT_CONFIRM', showDeletePaymentAccountConfirmModal: !state.showDeletePaymentAccountConfirmModal, deletePaymentAccountID });
};

var togglePaymentTypeSelectModal = function (dispatch, getState) {
    const state = getState().payment;
    dispatch({ type: 'TOGGLE_PAYMENT_TYPE_SELECTED_MODAL', showPaymentTypeSelectedModal: !state.showPaymentTypeSelectedModal });
};

var setPaymentType = function (dispatch, paymentAccounts) {
    let paymentsAccountsToCheck;
    if (paymentAccounts && paymentAccounts.length > 0) {
        //get credit card and eCheck payment accounts
        paymentsAccountsToCheck = paymentAccounts.filter((item) => item.paymentAccountTypeID == 3 || item.paymentAccountTypeID == 4);

        //all elements are credit card
        if (paymentsAccountsToCheck.every((item) => item.paymentAccountTypeID == 3)) {
            dispatch({ type: 'PAYMENT_TYPE_CREDIT_CARD', showCreditCardPaymentType: true });
        }
        //all elements are eCheck
        else if (paymentsAccountsToCheck.every((item) => item.paymentAccountTypeID == 4)) {
            dispatch({ type: 'PAYMENT_TYPE_ECHECK', showEcheckPaymentType: true });
        }
        //there are both credit card and eCheck
        else if (paymentsAccountsToCheck.some((item) => item.paymentAccountTypeID == 3)
            && paymentsAccountsToCheck.some((item) => item.paymentAccountTypeID == 4)) {
            dispatch({ type: 'PAYMENT_TYPE_CREDITCARD_AND_ECHECK', showCreditCardAndEcheckPaymentType: true });
        }
        else {
            dispatch({ type: 'PAYMENT_TYPE_CREDIT_CARD', showCreditCardPaymentType: true });
        }
    }
    else {
        dispatch({ type: 'PAYMENT_TYPE_CREDIT_CARD', showCreditCardPaymentType: true });
    }
};

export const reducer = (state, action) => {
    state = state || initialState;


    switch (action.type) {
        case 'REQUEST_PAYMENT_START':
            return { ...state, paymentLoading: true, paymentErrorMessage: '' };
        case 'SET_PAYMENT_ERROR':
            return { ...state, paymentErrorMessage: action.paymentErrorMessage || action.error, paymentLoading: false, authPaymentVerified: true };
        case 'HIDE_LOADING_SPINNER':
            return { ...state, paymentLoading: false };
        case 'SET_PAYMENT_ACCOUNTS':
            return { ...state, paymentLoading: false, hasRequestedPaymentAccounts: true, paymentAccounts: action.paymentAccounts };
        case 'SET_PAYMENT_ACCOUNT':
            return { ...state, paymentAccount: action.paymentAccount };
        case 'SET_ACCOUNT_PAY_PAYMENT_ACCOUNT_CC':
            return { ...state, accountPayCreditCardPaymentAccount: action.accountPayCreditCardPaymentAccount };
        case 'SET_ACCOUNT_PAY_PAYMENT_ACCOUNT_ECHECK':
            return { ...state, accountPayEcheckPaymentAccount: action.accountPayEcheckPaymentAccount };
        case 'SET_ACCOUNT_AUTO_PAYMENT':
            return { ...state, paymentLoading: false, accountAutoPayment: action.accountAutoPayment };
        case 'SET_PAYMENT_EXTENSION':
            return { ...state, paymentLoading: false, paymentArrangement: action.paymentArrangement };
        case 'RESPONSE_PAYMENT_EXTENSION_DATA':
            return { ...state, paymentLoading: false, paymentArrangement: action.paymentArrangement, payExtensionRenderData: action.payExtensionRenderData, paymentErrorMessage: action.paymentErrorMessage };
        case 'TOGGLE_ADD_PAYMENT_METHOD':
            return { ...state, showAddPaymentMethod: action.showAddPaymentMethod };
        case 'TOGGLE_SHOW_AUTO_PAYMENT_FORM':
            return { ...state, showAutoPayForm: action.showAutoPayForm };
        case 'TOGGLE_STOP_AUTO_PAYMENT_CONFIRM':
            return { ...state, showStopAutoPayConfirmModal: action.showStopAutoPayConfirmModal };
        case 'SET_ACCOUNT_PAY_SUCCESS':
            return { ...state, paymentLoading: false, accountPaymentSuccess: action.accountPaymentSuccess, accountPayConfirmationNumber: action.accountPayConfirmationNumber, authPaymentVerified: false };
        case 'TOGGLE_ACCOUNT_PAYMENT_SUCCESS_MODAL':
            return { ...state, showAccountPaymentSuccessModal: action.showAccountPaymentSuccessModal };
        case 'TOGGLE_DELETE_PAYMENT_ACCOUNT_CONFIRM':
            return { ...state, showDeletePaymentAccountConfirmModal: action.showDeletePaymentAccountConfirmModal, deletePaymentAccountID: action.deletePaymentAccountID };
        case 'TOGGLE_PAYMENT_EXTENSION_RESULT_CONFIRM':
            return { ...state, showConfirmModal: action.showConfirmModal, paymentLoading: action.paymentLoading };
        case 'SET_ACCOUNT_REDIRECT_TO_AUTOPAY':
            return { ...state, redirectToAutoPay: action.redirectToAutoPay, showPaymentAccountCreated: action.showPaymentAccountCreated };
        case 'RESPONSE_PAYMENT_HISTORY':
            return { ...state, paymentLoading: false, paymentHistoryList: action.paymentHistoryList, phYears: action.years };
        case 'RESPONSE_I4GO_RESULT':
            return { ...state, paymentLoading: false, i4GoAuthorizeClient: action.i4GoAuthorizeClient };
        case 'CLEAR_PAYMENT_STATE':
            return initialState;
        case 'SET_SUGGESTED_PAYMENT_OBJECT':
            return { ...state, accountLoading: false, suggestedPaymentObject: action.suggestedPaymentObject };
        case 'TOGGLE_PAYMENT_TYPE_SELECTED_MODAL':
            return { ...state, showPaymentTypeSelectedModal: action.showPaymentTypeSelectedModal };
        case 'TOGGLE_PAYMENT_TYPE_SELECTED_MODAL':
            return { ...state, showPaymentTypeSelectedModal: action.showPaymentTypeSelectedModal };
        case 'PAYMENT_TYPE_CREDIT_CARD':
            return { ...state, showCreditCardPaymentType: action.showCreditCardPaymentType };
        case 'PAYMENT_TYPE_ECHECK':
            return { ...state, showEcheckPaymentType: action.showEcheckPaymentType };
        case 'PAYMENT_TYPE_CREDITCARD_AND_ECHECK':
            return { ...state, showCreditCardAndEcheckPaymentType: action.showCreditCardAndEcheckPaymentType };
        case 'REQUEST_AUTH_PAYMENT_START':
            return { ...state, authorizeAccountPaymentLoading: true };
        case 'AUTH_PAYMENT_SUCCESS':
            return { ...state, authorizeAccountPaymentLoading: false, authPaymentVerified: true };
        case 'AUTH_PAYMENT_EXPIRED':
            return { ...state, authorizeAccountPaymentLoading: false, authPaymentExpired: true, authPaymentVerified: false };
        case 'SET_AUTH_PAYMENT_ERROR':
            return { ...state, authorizeAccountPaymentLoading: false, paymentErrorMessage: action.paymentErrorMessage, authPaymentVerified: false };
        case 'AUTH_PAYMENT_BYPASS':
            return { ...state, authorizeAccountPaymentLoading: false, authPaymentVerified: true };
        // iFrame related cases
        case 'REQUEST_HOSTED_PAYMENT_PAGE_CONFIG_START':
            return { ...state, paymentLoading: true, paymentErrorMessage: '' };
        case 'SET_HOSTED_PAYMENT_PAGE_CONFIG':
            return {
                ...state,
                paymentLoading: action.isLoading,
                useHostedPaymentPage: action.config.useHostedPaymentPage,
                hostedPaymentPageContent: action.config.hostedPaymentPageContent,
                paymentProviderID: action.config.paymentProviderID,
                vendorName: action.config.vendorName
            };
        case 'SET_HOSTED_PAYMENT_PAGE_CONFIG_ERROR':
            return {
                ...state,
                paymentLoading: false,
                paymentErrorMessage: action.error,
                useHostedPaymentPage: false
            };

        case 'SET_PAYMENT_ATTEMPT_ID':
            return { ...state, paymentAttemptID: action.paymentAttemptID };
        case 'REQUEST_HOSTED_PAYMENT_VALIDATION_RESPONSE':
            return {
                ...state,
                paymentLoading: action.isLoading,
                paymentErrorMessage: '',
                paymentValidationResult: action.paymentValidationResult,
                // Reset the attempt ID whenever validation is reset (user went Back)
                paymentAttemptID: action.paymentValidationResult === PaymentValidationResult.Unset ? 0 : state.paymentAttemptID
            };
        case 'SET_IFRAME_TOKEN':
            return { ...state, iFrameToken: action.token };
        case 'SET_IFRAME_RESPONSE':
            return { ...state, iFrameResponse: action.response };
        case 'SET_PAYMENT_SUCCESS':
            return { ...state, paymentLoading: false, paymentErrorMessage: '', paymentSuccessMessage: action.message };
        default:
            return state;
    }
};
